import { useMyOrderIntentionsContext } from 'contexts/MyOrderIntentionsContext';
import { OrderIntentionOriginEnum } from 'enums/orderIntentionOrigin.enum';
import { OrderIntentionStatusEnum } from 'enums/orderIntentionStatus.enum';
import { OrderIntention } from 'model/OrderIntention';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as MyOrderIntentionEditCalls from 'store/state/myOrderIntentionEdit/apiCalls';
import { actions as MyOrderIntentionEditActions } from 'store/state/myOrderIntentionEdit/myOrderIntentionEditSlice';
import { sessionSelectors } from 'store/state/selectors';
import {
  hasValidDeliveryDatesForRepublish,
  isDeliveryDateExpiredForRepublish,
} from './orderIntentionRules';

export interface OrderIntentionAction {
  key: string;
  label: string;
  onSelect: () => void | Promise<unknown>;
}

const isPendingDirectSale = (order: OrderIntention) =>
  order.origin === OrderIntentionOriginEnum.DIRECT_SALE &&
  order.status === OrderIntentionStatusEnum.PENDENTE;

const isOrderIntentionWith = (
  order: OrderIntention,
  ...statuses: OrderIntentionStatusEnum[]
) =>
  order.origin === OrderIntentionOriginEnum.ORDER_INTENTION &&
  statuses.includes(order.status);

const canEdit = (order: OrderIntention) =>
  isOrderIntentionWith(
    order,
    OrderIntentionStatusEnum.EXPIRADO,
    OrderIntentionStatusEnum.RECUSADO,
  ) ||
  (isOrderIntentionWith(order, OrderIntentionStatusEnum.CANCELADO) &&
    !order.orderNumber);

export function useOrderIntentionRowActions() {
  const dispatch = useDispatch();
  const { setShowDetailsModal, setSelectedOrderIntentions } =
    useMyOrderIntentionsContext();
  const { t } = useTranslation();
  const { customers } = useSelector(sessionSelectors.getRoot);
  const selectedCustomerId = useSelector(
    sessionSelectors.getSelectedCustomerId,
  );
  const [customerSelected] = useState(
    customers.find((customer) => customer.id === selectedCustomerId),
  );

  return (record: OrderIntention): OrderIntentionAction[] => {
    const actions: OrderIntentionAction[] = [];

    if (isPendingDirectSale(record)) {
      actions.push({
        key: 'approve',
        label: 'Aprovar',
        onSelect: () => {
          dispatch(MyOrderIntentionEditActions.setShowModalAprove(true));
          dispatch(MyOrderIntentionEditActions.setIdsBulkAprove([record.id]));
        },
      });
      actions.push({
        key: 'refuse',
        label: 'Recusar',
        onSelect: () => {
          if (!record.justification) {
            dispatch(
              MyOrderIntentionEditActions.setShowModalNoJustification(true),
            );
          } else {
            dispatch(
              MyOrderIntentionEditActions.setJustificationText(
                record.justification ?? '',
              ),
            );
            dispatch(MyOrderIntentionEditActions.setShowModalReprove(true));
            dispatch(
              MyOrderIntentionEditActions.setIdsBulkReprove([record.id]),
            );
          }
        },
      });
    }

    if (isOrderIntentionWith(record, OrderIntentionStatusEnum.EXPIRADO)) {
      actions.push({
        key: 'republish',
        label: 'Republicar',
        onSelect: () => {
          if (isDeliveryDateExpiredForRepublish(record.deliveryDate)) {
            dispatch(MyOrderIntentionEditActions.setShowModalNoRepublish(true));
          } else {
            dispatch(MyOrderIntentionEditActions.setShowModalRepublish(true));
            dispatch(MyOrderIntentionEditActions.setIdRepublish([record.id]));
          }
        },
      });
    }

    if (canEdit(record)) {
      actions.push({
        key: 'edit',
        label: 'Editar',
        onSelect: () => {
          const fetchOffers = dispatch(
            MyOrderIntentionEditCalls.fetchOffersByProducts({
              order: record,
              accountCode: customerSelected?.accountCode,
            }),
          ) as unknown as Promise<unknown>;
          dispatch(
            MyOrderIntentionEditActions.setFormInfo({
              customerId: record.customerId,
              customerCode: record.customerCode,
              customerName: record.customerName,
              commercialDate: record.commercialDate,
              deliveryDate: record.deliveryDate,
              id: record.id,
            }),
          );
          return fetchOffers;
        },
      });
    }

    if (
      isOrderIntentionWith(record, OrderIntentionStatusEnum.AGUARDANDO_PRODUTOR)
    ) {
      actions.push({
        key: 'cancel',
        label: 'Cancelar IC',
        onSelect: () => {
          dispatch(MyOrderIntentionEditActions.setShowModalCancel(true));
          dispatch(MyOrderIntentionEditActions.setIdCancel([record.id]));
        },
      });
    }

    actions.push({
      key: 'details',
      label: t('myOrderIntentions.table.rowDropdown.seeDetails'),
      onSelect: () => {
        setShowDetailsModal(true);
        setSelectedOrderIntentions([record]);
      },
    });

    return actions;
  };
}

export function useOrderIntentionBulkActions(): OrderIntentionAction[] {
  const dispatch = useDispatch();
  const { selectedOrderIntentionsKeys, selectedOrderIntentions } =
    useMyOrderIntentionsContext();
  const [first] = selectedOrderIntentions;
  if (!first) return [];

  const actions: OrderIntentionAction[] = [];

  if (isPendingDirectSale(first)) {
    actions.push({
      key: 'approve',
      label: 'Aprovar',
      onSelect: () => {
        dispatch(MyOrderIntentionEditActions.setShowModalAprove(true));
        dispatch(
          MyOrderIntentionEditActions.setIdsBulkAprove(
            selectedOrderIntentionsKeys,
          ),
        );
      },
    });
  }

  if (isOrderIntentionWith(first, OrderIntentionStatusEnum.EXPIRADO)) {
    actions.push({
      key: 'republish',
      label: 'Republicar',
      onSelect: () => {
        if (hasValidDeliveryDatesForRepublish(selectedOrderIntentions)) {
          dispatch(MyOrderIntentionEditActions.setShowModalRepublish(true));
          dispatch(
            MyOrderIntentionEditActions.setIdRepublish(
              selectedOrderIntentionsKeys,
            ),
          );
        } else {
          dispatch(MyOrderIntentionEditActions.setShowModalNoRepublish(true));
        }
      },
    });
  }

  if (
    isOrderIntentionWith(first, OrderIntentionStatusEnum.AGUARDANDO_PRODUTOR)
  ) {
    actions.push({
      key: 'cancel',
      label: 'Cancelar IC',
      onSelect: () => {
        dispatch(MyOrderIntentionEditActions.setShowModalCancel(true));
        dispatch(
          MyOrderIntentionEditActions.setIdCancel(selectedOrderIntentionsKeys),
        );
      },
    });
  }

  return actions;
}
